import { isDefined } from 'ts-utils';
import { ActionType } from '../../store/ActionTypes';
import { store, StoreStateInternal, useAppDispatch } from '../../store/Store';
import { getScreenAnswers } from '../../util/ConditionalNextScreenSelectors';

/** Returns a key defined in `nextScreen`. When multiple keys are returned, the first one found in
 * `nextScreen` is used. */
export type RouteSelector<Key extends string> = (
  state: StoreStateInternal,
  currentScreenName: string
) => Key | Key[] | undefined;

/** A screen name for each route key, and `fallback` for any key the map lacks. */
export type NextScreenMap<ScreenNames extends string, Key extends string = never> = Record<
  string,
  ScreenNames
> &
  Record<Key | 'fallback', ScreenNames>;

/** Type-only: carries a screen's outcome keys from its config type to its `NavigationProps`, so
 * they're declared once. Nothing sets it. */
declare const outcomeKeys: unique symbol;

/** `Outcome` is the keys a screen can report to `next`. Its `nextScreen` map must route each one. */
export type FlowRoute<ScreenNames extends string, Outcome extends string = never> = {
  screenName: ScreenNames;
  /** Defaults to the answers given on the current screen (getScreenAnswers). Pair an explicit
   * selector with its map using `routeBy`. */
  nextScreenSelector?: RouteSelector<string>;
} & ([Outcome] extends [never]
  ? { nextScreen?: ScreenNames | NextScreenMap<ScreenNames> }
  : { nextScreen: NextScreenMap<ScreenNames, Outcome>; readonly [outcomeKeys]?: Outcome });

/**
 * Pairs a selector with its `nextScreen` map, so a key the selector can return but the map lacks
 * fails to compile instead of falling through to `fallback`. The map may also route outcome keys.
 */
export function routeBy<
  Key extends string,
  // Never inferred, but typing the map's values by a type parameter keeps them literal, so the
  // config can check each one is its screen.
  ScreenNames extends string,
  Map extends Record<Key | 'fallback', ScreenNames>,
>(
  nextScreenSelector: RouteSelector<Key>,
  nextScreen: Map
): { nextScreenSelector: RouteSelector<Key>; nextScreen: Map } {
  return { nextScreenSelector, nextScreen };
}

export type NextOptions<Outcome extends string> = {
  /** What happened on the screen, for its config's `nextScreen` map to route. */
  outcome?: Outcome;
  resetHistory?: boolean;
};

type FlowNavigator<ScreenNames extends string, Outcome extends string = never> = {
  next: (props: FlowRoute<ScreenNames>, options?: NextOptions<Outcome>) => void;
  goBack: () => void;
  goTo: (screen: ScreenNames | undefined, resetHistory?: boolean) => void;
};

/** The outcome keys a screen's config type declares (see `FlowRoute`). */
type OutcomeOf<Props> = Props extends { readonly [outcomeKeys]?: infer Outcome }
  ? Extract<Outcome, string>
  : never;

/** `next` takes the outcome keys `Props`, the screen's config type, declares. */
export type NavigationProps<Props, ScreenNames extends string> = {
  navigation: FlowNavigator<ScreenNames, OutcomeOf<Props>>;
  onShowToast?: () => void;
} & Props;

/** `state` defaults to the app store; the persona walker passes its own to see what routing reads.
 * An `outcome` the screen reports is routed ahead of the selector. */
export function resolveNextScreen<ScreenNames extends string>(
  props: FlowRoute<ScreenNames>,
  state: StoreStateInternal = store.getState(),
  outcome?: string
): ScreenNames | undefined {
  const { nextScreen, screenName, nextScreenSelector = getScreenAnswers } = props;
  if (nextScreen === undefined) {
    return undefined;
  }
  if (typeof nextScreen === 'string') {
    return nextScreen;
  }
  // Else conditional navigation
  // If multiple options are selected, prioritise routing to any with explicit conditional routes
  const nextScreenKeys =
    outcome === undefined ? toArray(nextScreenSelector(state, screenName)) : [outcome];
  const nextScreenKey =
    nextScreenKeys.find((key) => isDefined(key) && isDefined(nextScreen[key])) ?? 'fallback';
  const nextScreenName = nextScreen[nextScreenKey];

  if (nextScreenName === undefined) {
    console.error(
      `FlowNavigator: No ${screenName} screen route or fallback defined for key '${nextScreenKey}'. ` +
        `Keys include: '${Object.keys(nextScreen).join("', '")}'.`
    );
  }

  return nextScreenName;
}

export function useFlowNavigator<ScreenNames extends string>(): FlowNavigator<ScreenNames, string> {
  const dispatch = useAppDispatch();

  function next<ScreenNames extends string>(
    props: FlowRoute<ScreenNames>,
    { outcome, resetHistory = false }: NextOptions<string> = {}
  ) {
    const screen = resolveNextScreen(props, store.getState(), outcome);
    navigateToScreen(screen, resetHistory);
  }

  function goBack() {
    dispatch({ type: ActionType.DECREMENT_PAGE });
  }

  function navigateToScreen<ScreenNames extends string>(
    screen: ScreenNames | undefined,
    resetHistory = false
  ) {
    if (screen === undefined) {
      console.error(`FlowNavigator: Attempting to navigate to undefined screen: ${screen}.`);
    } else {
      dispatch({ type: ActionType.SET_PAGE, page: screen, resetHistory });
    }
  }

  return { next, goBack, goTo: navigateToScreen };
}

function toArray<T>(value: T | T[]): T[] {
  return Array.isArray(value) ? value : [value];
}
